import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, BASKET_WIDTH } from '../config/constants';
import { getLevel, LEVELS } from '../data/levels';
import { pickRandomItem } from '../data/items';
import { ItemDef, LevelConfig, LevelResult } from '../types';
import { Sfx } from '../systems/Sfx';
import { Music } from '../systems/Music';
import { calculateStars, isCorrectCatch, scoreCatch } from '../systems/Scoring';
import { trackLevelStarted } from '../systems/Telemetry';

interface FallingItem extends Phaser.Physics.Arcade.Image {
  itemDef: ItemDef;
}

/**
 * Core MVP loop: items fall from the portal and the player positions a
 * two-compartment basket beneath them. The left compartment sorts Halloween
 * items, the right compartment sorts Christmas items, and fusion items fit
 * either compartment.
 */
/** How quickly the basket eases toward its target x each second (higher = snappier). */
const BASKET_EASE_RATE = 14;

export class GameScene extends Phaser.Scene {
  private level!: LevelConfig;
  private basket!: Phaser.Physics.Arcade.Image;
  private basketTargetX = GAME_WIDTH / 2;
  private halloweenLabel!: Phaser.GameObjects.Text;
  private christmasLabel!: Phaser.GameObjects.Text;
  private mascot!: Phaser.GameObjects.Image;
  private mascotResetEvent?: Phaser.Time.TimerEvent;
  private items!: Phaser.Physics.Arcade.Group;
  private portal!: Phaser.GameObjects.Image;

  private score = 0;
  private combo = 0;
  private bestCombo = 0;
  private spawned = 0;
  private itemsResolved = 0;
  private spawnTimer?: Phaser.Time.TimerEvent;

  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;

  constructor() {
    super('Game');
  }

  init(data: { levelId: number }): void {
    this.level = getLevel(data.levelId ?? 1);
    trackLevelStarted(this.level.id);
    this.score = 0;
    this.combo = 0;
    this.bestCombo = 0;
    this.spawned = 0;
    this.itemsResolved = 0;
  }

  create(): void {
    Music.play('game');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => Music.stop());

    // Background shifts from Halloween to Christmas palette as levels progress
    // (ASSET.md section 4 "background gradient shifts... as the player progresses").
    const progress = LEVELS.length > 1 ? (this.level.id - 1) / (LEVELS.length - 1) : 0;
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_halloweentown').setAlpha(0.9 * (1 - progress));
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_christmastown').setAlpha(0.9 * progress);

    this.portal = this.add.image(GAME_WIDTH / 2, 60, 'prop_portal').setScale(0.6);
    this.tweens.add({ targets: this.portal, angle: 360, duration: 6000, repeat: -1 });

    this.items = this.physics.add.group();

    this.basket = this.physics.add
      .image(GAME_WIDTH / 2, GAME_HEIGHT - 60, 'prop_basket')
      .setImmovable(true);
    (this.basket.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);

    this.mascot = this.add.image(this.basket.x, this.basket.y - 50, 'mascot_idle').setScale(0.5);
    this.halloweenLabel = this.createBasketLabel('🎃', -BASKET_WIDTH / 4);
    this.christmasLabel = this.createBasketLabel('❄', BASKET_WIDTH / 4);

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) this.moveBasketTo(pointer.x);
    });
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => this.moveBasketTo(pointer.x));

    const cursors = this.input.keyboard?.createCursorKeys();
    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      if (!cursors) return;
      const speed = 480 * (this.game.loop.delta / 1000);
      if (cursors.left.isDown) this.moveBasketTo(this.basket.x - speed);
      if (cursors.right.isDown) this.moveBasketTo(this.basket.x + speed);
    });

    this.physics.add.overlap(this.basket, this.items, (_basket, item) => {
      this.resolveCatch(item as FallingItem);
    });

    this.scoreText = this.add.text(24, 24, 'Score: 0', {
      fontFamily: 'Arial',
      fontSize: '28px',
      color: '#ffffff'
    });
    this.comboText = this.add
      .text(GAME_WIDTH - 24, 24, 'Combo: 0', { fontFamily: 'Arial', fontSize: '28px', color: '#ffffff' })
      .setOrigin(1, 0);

    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 12, 'Match each item with the correct half of the basket', {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#cccccc'
      })
      .setOrigin(0.5, 1);

    this.spawnTimer = this.time.addEvent({
      delay: this.level.spawnIntervalMs,
      loop: true,
      callback: () => this.spawnItem()
    });

    this.createPauseButton();
    this.input.keyboard?.on('keydown-ESC', () => this.pauseGame());
  }

  private createBasketLabel(label: string, offsetX: number): Phaser.GameObjects.Text {
    return this.add
      .text(this.basket.x + offsetX, this.basket.y + 6, label, {
        fontFamily: 'Arial',
        fontSize: '28px',
        color: '#ffffff',
        stroke: '#2e2a38',
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setDepth(2);
  }

  private createPauseButton(): void {
    const button = this.add
      .rectangle(GAME_WIDTH / 2, 30, 48, 40, 0x000000, 0.35)
      .setStrokeStyle(2, 0xffffff, 0.6)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(GAME_WIDTH / 2, 30, '❚❚', { fontFamily: 'Arial', fontSize: '18px', color: '#ffffff' })
      .setOrigin(0.5);
    button.on('pointerdown', () => this.pauseGame());
  }

  private pauseGame(): void {
    Sfx.buttonTap();
    this.scene.pause();
    this.scene.launch('Pause', { levelId: this.level.id });
  }

  private moveBasketTo(x: number): void {
    const half = BASKET_WIDTH / 2;
    this.basketTargetX = Phaser.Math.Clamp(x, half, GAME_WIDTH - half);
  }

  /** Briefly swaps the mascot to a reaction pose, then back to idle. */
  private reactMascot(texture: 'mascot_happy' | 'mascot_oops'): void {
    this.mascot.setTexture(texture);
    this.mascotResetEvent?.remove();
    this.mascotResetEvent = this.time.delayedCall(400, () => this.mascot.setTexture('mascot_idle'));
  }

  private spawnItem(): void {
    if (this.spawned >= this.level.itemCount) {
      this.spawnTimer?.remove();
      return;
    }
    this.spawned++;

    const def = pickRandomItem(this.level.itemPool);
    // Keeping spawns inside this shared range guarantees that either basket
    // compartment can be aligned beneath every item, including near the edges.
    const spawnMargin = Math.ceil(BASKET_WIDTH * 0.75);
    const x = Phaser.Math.Between(spawnMargin, GAME_WIDTH - spawnMargin);
    const item = this.items.create(x, 90, def.key) as FallingItem;
    item.itemDef = def;
    item.setVelocityY(this.level.fallSpeed);
    (item.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
  }

  update(): void {
    const easeFactor = 1 - Math.exp(-BASKET_EASE_RATE * (this.game.loop.delta / 1000));
    this.basket.x = Phaser.Math.Linear(this.basket.x, this.basketTargetX, easeFactor);
    this.mascot.x = this.basket.x;
    this.halloweenLabel.x = this.basket.x - BASKET_WIDTH / 4;
    this.christmasLabel.x = this.basket.x + BASKET_WIDTH / 4;

    this.items.getChildren().forEach((child) => {
      const item = child as FallingItem;
      if (item.y > GAME_HEIGHT + 40) {
        this.resolveMiss(item);
      }
    });
  }

  private resolveCatch(item: FallingItem): void {
    if (!item.active) return;
    const compartment = item.x < this.basket.x ? 'halloween' : 'christmas';
    const correct = isCorrectCatch(item.itemDef.family, compartment);

    if (correct) {
      this.combo++;
      this.bestCombo = Math.max(this.bestCombo, this.combo);
      this.score += scoreCatch(item.itemDef.points, this.combo);
      this.spawnPop(item.x, item.y, 0x9dffb0);
      this.reactMascot('mascot_happy');
      if (this.combo > 0 && this.combo % 5 === 0) {
        Sfx.comboUp(this.combo / 5);
      } else {
        Sfx.catchCorrect();
      }
    } else {
      this.combo = 0;
      this.spawnPop(item.x, item.y, 0xff6666);
      this.reactMascot('mascot_oops');
      Sfx.catchWrong();
    }

    this.updateHud();
    this.popAndDestroy(item);
    this.itemsResolved++;
    this.checkLevelEnd();
  }

  /** Squash-and-stretch bounce before removing a caught item (ASSET.md section 7). */
  private popAndDestroy(item: FallingItem): void {
    (item.body as Phaser.Physics.Arcade.Body).enable = false;
    this.tweens.add({
      targets: item,
      scaleX: 1.35,
      scaleY: 0.7,
      duration: 90,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => item.destroy()
    });
  }

  private resolveMiss(item: FallingItem): void {
    if (!item.active) return;
    this.combo = 0;
    this.reactMascot('mascot_oops');
    Sfx.itemMiss();
    this.updateHud();
    item.destroy();
    this.itemsResolved++;
    this.checkLevelEnd();
  }

  private spawnPop(x: number, y: number, tint: number): void {
    const emitter = this.add.particles(x, y, 'fx_particle', {
      speed: { min: 80, max: 180 },
      scale: { start: 1, end: 0 },
      lifespan: 300,
      quantity: 8,
      tint
    });
    this.time.delayedCall(320, () => emitter.destroy());
  }

  private updateHud(): void {
    this.scoreText.setText(`Score: ${this.score}`);
    this.comboText.setText(`Combo: ${this.combo}`);
  }

  private checkLevelEnd(): void {
    if (this.spawned >= this.level.itemCount && this.itemsResolved >= this.level.itemCount) {
      const stars = calculateStars(this.score, this.level.starThresholds);

      const result: LevelResult = {
        levelId: this.level.id,
        score: this.score,
        stars,
        bestCombo: this.bestCombo
      };

      this.time.delayedCall(400, () => this.scene.start('LevelComplete', { result }));
    }
  }
}
